import React, { useState, useRef } from 'react';
import { User, Save, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '../../App';

const ProfilePage = () => {
  const { user, updateUser, token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    handicap: user?.handicap || '',
    phone: user?.phone || ''
  });

  // Generate profile picture URL
  const getProfilePictureUrl = () => {
    if (!user?.profile_picture_url) return null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    return `${backendUrl}/api/files/${user.profile_picture_url}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const params = new URLSearchParams();
      if (formData.full_name) params.append('full_name', formData.full_name);
      if (formData.handicap) params.append('handicap', formData.handicap);
      if (formData.phone) params.append('phone', formData.phone);

      const response = await API.put(`/auth/profile?${params.toString()}`);
      updateUser(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingPicture(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await API.post('/auth/profile-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      updateUser(response.data);
      toast.success('Profile picture updated!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const profilePictureUrl = getProfilePictureUrl();

  return (
    <div data-testid="profile-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-2">Profile</h1>
        <p className="text-[#57534E]">Manage your account information and preferences.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-[#E7E5E4] p-8 text-center">
            {/* Profile Picture with Upload */}
            <div className="relative inline-block mb-6">
              <div 
                onClick={handlePictureClick}
                className="w-24 h-24 bg-[#2C5530] text-white flex items-center justify-center mx-auto cursor-pointer group relative overflow-hidden rounded-full"
                data-testid="profile-picture-container"
              >
                {uploadingPicture ? (
                  <Loader2 className="w-8 h-8 animate-spin" />
                ) : profilePictureUrl ? (
                  <img 
                    src={profilePictureUrl} 
                    alt={user?.full_name || 'Profile'} 
                    className="w-full h-full object-cover"
                    data-testid="profile-picture-img"
                  />
                ) : (
                  <User className="w-12 h-12" />
                )}
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="w-6 h-6 text-white" />
                </div>
              </div>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileChange}
                className="hidden"
                data-testid="profile-picture-input"
              />
            </div>
            
            <button
              onClick={handlePictureClick}
              disabled={uploadingPicture}
              className="text-sm text-[#2C5530] hover:text-[#1E3A21] font-medium mb-4 disabled:opacity-50"
              data-testid="change-picture-btn"
            >
              {uploadingPicture ? 'Uploading...' : 'Change Photo'}
            </button>
            
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-1">{user?.full_name}</h2>
            <p className="text-[#737373] text-sm mb-4">{user?.email}</p>
            {user?.handicap && (
              <div className="bg-[#F2F0E9] px-4 py-2 inline-block">
                <p className="text-xs uppercase tracking-wider text-[#737373]">Handicap</p>
                <p className="font-semibold text-[#1A1A1A]">{user.handicap}</p>
              </div>
            )}
            <p className="text-[#737373] text-sm mt-6">
              Member since {new Date(user?.created_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Edit Form */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-[#E7E5E4] p-8">
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-6">Edit Profile</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="profile-form">
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full"
                  data-testid="profile-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full bg-[#F2F0E9] text-[#737373] cursor-not-allowed"
                />
                <p className="text-xs text-[#737373] mt-1">Email cannot be changed</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="handicap" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Handicap
                  </label>
                  <input
                    type="number"
                    id="handicap"
                    name="handicap"
                    value={formData.handicap}
                    onChange={handleChange}
                    step="0.1"
                    min="-10"
                    max="54"
                    placeholder="e.g., 12.4"
                    className="w-full"
                    data-testid="profile-handicap-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+44 7000 000000"
                    className="w-full"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="bg-[#2C5530] text-white px-8 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
                data-testid="save-profile-btn"
              >
                {saving ? 'Saving...' : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Account Info */}
          <div className="bg-[#F2F0E9] p-6 mt-6">
            <h3 className="font-medium text-[#1A1A1A] mb-3">Account Information</h3>
            <ul className="space-y-2 text-sm text-[#57534E]">
              <li>• Your data is securely stored and never shared with third parties.</li>
              <li>• All session logs, game stats, and diary entries are private to your account.</li>
              <li>• For account deletion requests, please contact scott@quietmindgolf.co.uk</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
